import { parse as parseYaml } from 'yaml';

const PLACEHOLDER_TONES = ['mist', 'slate', 'silver', 'charcoal', 'fog'];

/** Resolves URLs under `public/` when deployed with non-root `vite.config base` */
function publicPath(rel) {
  const base = import.meta.env.BASE_URL;
  const trimmed = String(rel).replace(/^\//, '');
  const prefix = base.endsWith('/') ? base : `${base}/`;
  return `${prefix}${trimmed}`;
}

function photoUrl(segment, ...rest) {
  const Enc = encodeURIComponent;
  const path = [segment, ...rest].map((s) => Enc(String(s))).join('/');
  return publicPath(`photos/${path}`);
}

/** Block weird paths from roll manifests (`file:` values must stay in-folder). */
function safeRollImageFilename(raw) {
  if (raw === undefined || raw === null) return null;
  const name = String(raw).trim();
  if (!name) return null;
  const base = name.replace(/^[/\\]+/, '');
  if (!base || base.includes('..') || base.includes('/') || base.includes('\\')) return null;
  return base;
}

/**
 * Sidebar + galleries come only from valid `roll.yaml` + non-empty `title`.
 * Manifest `public/photos/rolls.json` is generated by `npm run photos:manifest`
 * (runs automatically before dev/build).
 *
 * @returns {Promise<import('./collections.js').Collection[]>}
 */
export async function loadGalleryCollections() {
  /** @type {{ id?: string }[]} */
  let manifestRolls = [];

  try {
    const manifestRes = await fetch(publicPath('photos/rolls.json'));
    if (manifestRes.ok) {
      const body = await manifestRes.json();
      manifestRolls = Array.isArray(body?.rolls) ? body.rolls : [];
    }
  } catch (_) {
    // no manifest → zero rolls in UI
  }

  const ids = manifestRolls
    .map((r) => (typeof r?.id === 'string' ? r.id.trim() : ''))
    .filter(Boolean);

  /** @type {import('./collections.js').Collection[]} */
  const out = [];

  for (const id of ids) {
    try {
      const res = await fetch(photoUrl(id, 'roll.yaml'));
      if (!res.ok) continue;

      const data = parseYaml(await res.text());
      const title =
        typeof data?.title === 'string' ? data.title.trim() : '';

      // Heading must live in YAML; otherwise this roll doesn’t appear in the UI
      if (!title) continue;

      /** @type {import('./collections.js').Frame[]} */
      const frames = [];
      const rawFrames = Array.isArray(data?.frames) ? data.frames : [];

      for (let i = 0; i < rawFrames.length; i++) {
        const f = rawFrames[i] ?? {};
        const file = safeRollImageFilename(f.file);
        const alt =
          typeof f.alt === 'string' && f.alt.trim()
            ? f.alt.trim()
            : file ?? `Frame ${i + 1}`;
        const caption =
          f.caption !== undefined && f.caption !== null ? String(f.caption) : undefined;

        /** @type {import('./collections.js').Frame} */
        const frame = { alt, caption };
        if (file) {
          frame.src = photoUrl(id, file);
        } else {
          const rawTone = typeof f.tone === 'string' ? f.tone.trim() : '';
          frame.tone = PLACEHOLDER_TONES.includes(rawTone) ? rawTone : 'mist';
        }
        frames.push(frame);
      }

      out.push({ id, title, frames });
    } catch (err) {
      console.warn(`[gallery] Skipping roll "${id}" (YAML missing or invalid):`, err);
    }
  }

  return out;
}
